import React, { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { DropDownArrow } from '@yme/assets/icons';
import { CustomScroll } from '@yme/atoms/custom-scroll';
import { PaginationContext } from '@yme/atoms/pagination/paginationContext';
import { SVGIcon } from '@yme/atoms/svg-icon';
import { colorBlackTransparent50, colorCoreBlack900Base, colorCoreGray300, colorCoreGray500, colorCoreGray800, colorCoreWhite1000, colorWhiteTransparent300, defaultTransparent, } from '@yme/tokens/colors';
import { CORNER_RADIUS } from '@yme/tokens/cornerRadius';
import { elevationBottomLvl2 } from '@yme/tokens/elevations';
import { ADAPTIVE } from '@yme/tokens/screen';
import { RegularTextLevel4 } from '@yme/tokens/typography';
import { Z_INDEX } from '@yme/tokens/zindex';
import { buildKey } from '@yme/utils/common';
import { getTextStyles } from '@yme/utils/styles';
const PerPageStyled = styled.div `
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  gap: 16px;

  ${ADAPTIVE.maxWidth.mobile} {
    align-self: self-end;
  }

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;
const DropDownWrapper = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
`;
const DropDown = styled.div `
  display: flex;
  box-sizing: border-box;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  width: 100%;
  height: 44px;

  padding: 10px 10px 10px 12px; /* TODO: need improve this styles/approach for RTL option */

  border-radius: ${CORNER_RADIUS.s};
  color: ${colorCoreBlack900Base};
  background-color: ${colorWhiteTransparent300};

  border-style: solid;
  border-width: 1px;
  border-color: ${({ isDropDownExpanded }) => isDropDownExpanded ? colorCoreGray800 : colorCoreGray500};

  &:hover {
    border-color: ${colorCoreGray800};
  }

  /* Alternative approach for style - "gap: 8px;" */
  & > span {
    margin: 0 4px;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }
`;
const ValueStyled = styled.span `
  ${getTextStyles('Ubuntu', 400, 14, 20)}

  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;
const DropDownListHolder = styled.div `
  position: absolute;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  display: ${({ isDropDownExpanded }) => (isDropDownExpanded ? 'flex' : 'none')};
  z-index: ${Z_INDEX.level2};
  border-radius: ${CORNER_RADIUS.m};
  background-color: ${colorCoreWhite1000};

  ${({ isTopDirection }) => isTopDirection
    ? css `
          bottom: 100%;
          margin-bottom: 8px;

          & > ul {
            display: flex;
            flex-flow: column-reverse;
          }
        `
    : css `
          margin-top: 8px;
          top: 100%;
        `};
`;
const DropDownList = styled.ul `
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  margin: 0;
  width: 100%;
  list-style: none;

  box-shadow: ${elevationBottomLvl2};
  border-radius: ${CORNER_RADIUS.m};

  ${({ withScroll }) => (withScroll ? CustomScroll : 'padding: 6px;')};
`;
const DropDownItem = styled.li `
  ${getTextStyles('Ubuntu', 400, 14, 20)}

  display: flex;
  flex-direction: row;
  position: relative;
  box-sizing: border-box;
  align-items: center;
  padding: 10px 12px;
  width: 100%;
  cursor: pointer;

  border-radius: ${CORNER_RADIUS.xs};
  color: ${colorCoreBlack900Base};

  background-color: ${({ isActiveTab }) => isActiveTab ? colorBlackTransparent50 : defaultTransparent};

  &:hover {
    background-color: ${colorCoreGray300};
  }
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SVGIconStyled = styled(({ isDropDownExpanded, ...props }) => React.createElement(SVGIcon, { ...props })) `
  transform: ${({ isDropDownExpanded }) => isDropDownExpanded ? 'rotate(0deg)' : 'rotate(180deg)'};
`;
const MAX_HEIGHT_DROP_DOWN_LIST = 220;
export function PerPage({ isRightToLeft = false, perPageLabel = '', currentPerPageValue, setCurrentPerPageValue, perPageOptions, }) {
    const [isDropDownExpanded, setIsDropDownExpanded] = useState(false);
    const [isUpwardDirection, setIsUpwardDirection] = useState(false);
    const dropDownListRef = useRef(null);
    const dropDownRef = useRef(null);
    const { setPerPageLimit } = useContext(PaginationContext);
    const onClickHandler = (id) => {
        setCurrentPerPageValue(id);
        setIsDropDownExpanded(false);
    };
    const onClickDropDownHandler = () => {
        setIsDropDownExpanded(!isDropDownExpanded);
        const offsetOptions = dropDownRef?.current?.getBoundingClientRect();
        if (offsetOptions && offsetOptions?.bottom > MAX_HEIGHT_DROP_DOWN_LIST) {
            setIsUpwardDirection(true);
        }
        else {
            setIsUpwardDirection(false);
        }
    };
    useEffect(() => {
        if (currentPerPageValue) {
            setPerPageLimit(currentPerPageValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPerPageValue]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            const isOutsideClick = dropDownListRef?.current &&
                !dropDownListRef.current?.contains(e.target) &&
                !dropDownRef.current?.contains(e.target);
            if (isOutsideClick) {
                setIsDropDownExpanded(false);
            }
        };
        window.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropDownListRef, dropDownRef]);
    if (!perPageOptions?.length) {
        return null;
    }
    return (React.createElement(PerPageStyled, null,
        perPageLabel && React.createElement(RegularTextLevel4, { weight: 500 }, perPageLabel),
        React.createElement(DropDownWrapper, null,
            React.createElement(DropDown, { onClick: onClickDropDownHandler, ref: dropDownRef, isDropDownExpanded: isDropDownExpanded },
                React.createElement(ValueStyled, null, currentPerPageValue),
                React.createElement(SVGIconStyled, { type: DropDownArrow, isDropDownExpanded: isDropDownExpanded })),
            React.createElement(DropDownListHolder, { isDropDownExpanded: isDropDownExpanded, ref: dropDownListRef, isTopDirection: isUpwardDirection },
                React.createElement(DropDownList, { withScroll: perPageOptions?.length >= 8, isRightToLeft: isRightToLeft }, perPageOptions?.map((item, index) => {
                    const isActiveItem = currentPerPageValue === item;
                    return (React.createElement(DropDownItem, { key: buildKey(item, index), onClick: () => !isActiveItem && onClickHandler(item), isActiveTab: isActiveItem },
                        React.createElement("span", null, item)));
                }))))));
}
PerPage.displayName = 'PerPageComponent_Pagination';
